import { Link } from "react-router-dom";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ToolCard } from "../components/ToolCard.jsx";
import { FolderGrid } from "../components/FolderGrid.jsx";
import BackArrow from "../components/BackArrow.jsx";

const EM = "#F8B84E";       // locked NerdChem in-app accent (icon gradient start hue)
const EM_DEEP = "#7C3AED";  // family violet — makes the Ch badge gradient amber→violet
const EM_LIGHT = "#FCD9A0"; // light amber for gradient-text headings

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// Order + slugs per docs/family/01-nerdchem.md. All 9 chem tools live as of V45.
const TOOLS = [
  { name: "Periodic Table", path: "/chem/periodic-table", live: true, desc: "All 118 elements, interactive. Tap for full detail." },
  { name: "Molecular Weight", path: "/chem/molecular-weight", live: true, desc: "Parse a formula → molar mass. C6H12O6 → 180.16 g/mol." },
  { name: "Mole Calculator", path: "/chem/mole-calculator", live: true, desc: "Convert between mass, moles, and molecules." },
  { name: "pH / pOH Calculator", path: "/chem/ph-calculator", live: true, desc: "pH ↔ pOH ↔ [H⁺] ↔ [OH⁻]." },
  { name: "Dilution Calculator", path: "/chem/dilution", live: true, desc: "M₁V₁ = M₂V₂, solved any direction." },
  { name: "Significant Figures", path: "/chem/sig-figs", live: true, desc: "Sig-fig counter and rounding." },
  { name: "Equation Balancer", path: "/chem/equation-balancer", live: true, desc: "Balance any equation, exact whole-number coefficients." },
  { name: "Gas Law Solver", path: "/chem/gas-laws", live: true, desc: "Ideal & combined gas law, solve any variable." },
  { name: "Stoichiometry", path: "/chem/stoichiometry", live: true, desc: "Limiting reagent & theoretical yield." },
  { name: "Chemistry Reference", path: "/chem/reference", live: true, desc: "Concepts, reactions, procedures, trends — what to do and why." },
];

export default function ChemHome() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      {/* Bottom-edge stretched glow — V45.3 cross-family consistency, V62.1.1 broadened from centered circle */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: "70%", background: `radial-gradient(ellipse at center bottom, ${rgba(EM, 0.18)} 0%, transparent 70%)`, pointerEvents: "none" }} />

      <header style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px",
        paddingTop: "calc(16px + env(safe-area-inset-top))",
        paddingLeft: "calc(24px + env(safe-area-inset-left))",
        paddingRight: "calc(24px + env(safe-area-inset-right))",
        position: "relative", zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BackArrow to="/" from={EM} toColor={EM_LIGHT} />
          <div style={{ width: 38, height: 38, borderRadius: 11, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, color: "#fff" }}>Ch</div>
          {/* SVG wordmark — cross-browser-safe per V45.3 hero mockup work */}
          <svg width="115" height="24" viewBox="0 0 115 24" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
            <defs>
              <linearGradient id="wm-chem" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={EM_LIGHT} />
                <stop offset="1" stopColor="#A7F3D0" />
              </linearGradient>
            </defs>
            <text x="0" y="19" fill="url(#wm-chem)" fontFamily="DM Sans, system-ui, sans-serif" fontSize="20" fontWeight="800" letterSpacing="-0.5">NerdChem</text>
          </svg>
        </div>
      </header>

      <main style={{ maxWidth: 820, margin: "0 auto", padding: "40px 20px 80px", position: "relative", zIndex: 10 }}>
        <h1 style={{ fontSize: "clamp(32px, 6vw, 52px)", fontWeight: 800, letterSpacing: "-1.5px", textAlign: "center", margin: "0 0 14px", lineHeight: 1.1 }}>
          Chemistry tools.<br /><span style={{ background: `linear-gradient(135deg, ${EM_LIGHT}, ${EM})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Free. Fast. Actually good.</span>
        </h1>
        <p style={{ fontSize: 17, color: "#9CA3AF", textAlign: "center", maxWidth: 580, margin: "0 auto 14px", lineHeight: 1.6 }}>
          Chemistry by structure, not memorization. Periodic table, mole calculator, molecular weight, equation balancer, stoichiometry, pH, gas laws, dilution, sig figs — the calculators that do the math so you can see what's happening.
        </p>
        <p style={{ fontSize: 15, color: "#6F7290", textAlign: "center", maxWidth: 580, margin: "0 auto 44px", lineHeight: 1.6 }}>
          Free. No signup. No tracking. No data sale.
        </p>

        <FolderGrid ariaLabel="Chemistry tools">
          {TOOLS.map((t) => (
            <ToolCard
              key={t.name}
              to={t.live ? t.path : "/chem"}
              name={t.name}
              desc={t.desc}
              accent={EM}
            />
          ))}
        </FolderGrid>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
